// ==========================================
// BONIFICAÇÃO DO PARCEIRO — ponte entre a regra de 26/10/2026 e o banco
// ==========================================
// A conta em si mora em bonificacaoParceiro.ts (cópia aprovada do simulador —
// não mexer lá). Aqui ficam só as peças que transformam as negociações do
// banco em "consumidores do mês" e decidem o que gravar em comissao_parceiros:
//
//   - um consumidor = uma negociação; consumo = negociacoes.consumo_medio_kwh;
//     desconto = unidades_consumidoras.desconto_tarifa × 100 (todas as UCs
//     iguais; diferente ou vazio → não entra, com o motivo);
//   - a turma da faixa = mesmo parceiro dono + mesmo mês do aceite
//     (decisao_final_at no fuso de São Paulo), só quem é da regra nova, na ordem
//     do aceite. Venda do vendedor_parceiro conta na faixa do parceiro dele;
//   - Grupo A não pode assinar: fica fora da conta.
//
// Todo mundo é "Fixa" por enquanto (não existe campo de modalidade no
// contrato): Pix I + Pix II. O recorrente mensal da modalidade Variável
// (2% × redutor de cada fatura paga) ainda não entra na Folha.
//
// Arquivo puro (nada de supabase): testado com node:test em
// __tests__/bonificacaoParceiroFolha.test.ts.

import {
  REGRA_BONIFICACAO,
  calcularBonificacao,
  dataPagamentoAposFatura,
  dataPixI,
  motivoForaDaConta,
  redutor,
} from './bonificacaoParceiro.ts';
import { isGrupoTarifarioA } from './comissaoUtils.ts';
import { normalizeUcCodigo } from './ucCodigo.ts';

/** Valor gravado em comissao_parceiros.regra para a regra de 26/10/2026. */
export const REGRA_NOVA = '2026-10';

export const MOTIVO_SEM_DESCONTO = 'desconto não informado na UC';
export const MOTIVO_DESCONTOS_DIFERENTES = 'UCs com descontos diferentes: confira';
export const MOTIVO_GRUPO_A = 'Grupo A não pode assinar';

// ---------- datas (fuso de São Paulo) ----------

const FMT_SP = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'America/Sao_Paulo',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

export interface DiaCivil {
  ano: number;
  mes: number; // 1-12
  dia: number;
}

/**
 * Dia do calendário em São Paulo. Data pura ('2026-11-05') vale como está;
 * instante (timestamptz) é convertido — 31/10 às 22h em Brasília já é 01/11 em UTC.
 */
export function diaEmSaoPaulo(valor: string | Date | null | undefined): DiaCivil | null {
  if (!valor) return null;
  if (typeof valor === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(valor)) {
    const [ano, mes, dia] = valor.split('-').map(Number);
    return { ano, mes, dia };
  }
  const d = valor instanceof Date ? valor : new Date(valor);
  if (Number.isNaN(d.getTime())) return null;
  const partes = Object.fromEntries(FMT_SP.formatToParts(d).map(p => [p.type, p.value]));
  return { ano: Number(partes.year), mes: Number(partes.month), dia: Number(partes.day) };
}

const dois = (n: number) => String(n).padStart(2, '0');

/** YYYY-MM-DD com os componentes locais da data (sem passar por UTC). */
export function isoDia(d: Date): string {
  return `${d.getFullYear()}-${dois(d.getMonth() + 1)}-${dois(d.getDate())}`;
}

/** Consumidor aceito a partir de 26/10/2026 (fuso de São Paulo)? */
export function ehRegraNova(decisaoFinalAt: string | Date | null | undefined): boolean {
  const d = diaEmSaoPaulo(decisaoFinalAt);
  if (!d) return false;
  return `${d.ano}-${dois(d.mes)}-${dois(d.dia)}` >= REGRA_BONIFICACAO.vigenciaIso;
}

/** 1º dia do mês do aceite, 'YYYY-MM-01' (fuso de São Paulo). */
export function mesAceite(decisaoFinalAt: string | Date | null | undefined): string | null {
  const d = diaEmSaoPaulo(decisaoFinalAt);
  return d ? `${d.ano}-${dois(d.mes)}-01` : null;
}

/** Pix I: dia 10 do mês seguinte ao do aceite ('YYYY-MM-01' → 'YYYY-MM-10'). */
export function dataPrevistaPixI(mesDoAceite: string): string {
  const [ano, mes] = mesDoAceite.split('-').map(Number);
  return isoDia(dataPixI(new Date(ano, mes - 1, 1)));
}

/** Pix II (Fixa): dia 10 do mês seguinte ao pagamento da 1ª fatura. */
export function dataPrevistaPixII(pagamento: string | Date): string | null {
  const d = diaEmSaoPaulo(pagamento);
  return d ? isoDia(dataPagamentoAposFatura(new Date(d.ano, d.mes - 1, d.dia))) : null;
}

// ---------- quem recebe ----------

export interface PerfilDono {
  id: string;
  role?: string | null;
  parceiro_id?: string | null;
}

/**
 * Parceiro que recebe a bonificação da venda — o mesmo critério da Folha:
 * o próprio parceiro, ou o parceiro dono da rede quando quem vendeu é
 * vendedor_parceiro. Qualquer outro papel não gera bonificação de parceiro.
 */
export function donoDaVenda(responsavel: PerfilDono | null | undefined): string | null {
  if (!responsavel) return null;
  if (responsavel.role === 'parceiro') return responsavel.id;
  if (responsavel.role === 'vendedor_parceiro' && responsavel.parceiro_id) return responsavel.parceiro_id;
  return null;
}

// ---------- desconto da negociação ----------

export interface UcDaVenda {
  desconto_tarifa?: number | string | null;
  grupo_tarifario?: string | null;
}

export type DescontoDaVenda = { d: number } | { motivo: string };

/** Desconto da negociação em % — o mesmo em todas as UCs, senão não entra. */
export function descontoDaVenda(ucs: UcDaVenda[] | null | undefined): DescontoDaVenda {
  const lista = ucs || [];
  if (lista.length === 0) return { motivo: MOTIVO_SEM_DESCONTO };
  const valores: number[] = [];
  for (const uc of lista) {
    const bruto = uc.desconto_tarifa;
    if (bruto == null || bruto === '') return { motivo: MOTIVO_SEM_DESCONTO };
    const fracao = Number(bruto);
    if (!Number.isFinite(fracao)) return { motivo: MOTIVO_SEM_DESCONTO };
    // 0,28 × 100 = 28,000000000000004 no ponto flutuante
    valores.push(Math.round(fracao * 100 * 1e6) / 1e6);
  }
  if (new Set(valores).size > 1) return { motivo: MOTIVO_DESCONTOS_DIFERENTES };
  return { d: valores[0] };
}

// ---------- conta do mês ----------

export interface VendaBonificacao {
  negociacao_id: number;
  /** parceiro que recebe (ver donoDaVenda) */
  dono_id: string;
  decisao_final_at: string;
  consumo_medio_kwh: number | string | null | undefined;
  cpf_cnpj?: string | null;
  ucs: UcDaVenda[] | null | undefined;
}

export interface BonificacaoDaVenda {
  negociacao_id: number;
  dono_id: string;
  regra: typeof REGRA_NOVA;
  /** 'YYYY-MM-01' */
  mes_aceite: string;
  consumo: number;
  /** % de desconto (null quando não informado ou diferente entre as UCs) */
  desconto_percentual: number | null;
  /** null quando o desconto está fora da regra */
  redutor: number | null;
  fatura_com_desconto: number;
  /** faixa do mês do parceiro (F1..F6); null se ninguém da turma entra */
  faixa_mes: string | null;
  /** % da faixa (45 = 45% da fatura com desconto); 0 sem faixa */
  faixa_percentual: number;
  /** kWh somados da turma que entra na conta */
  kwh_mes: number;
  /** bonificação total (Pix I + Pix II) */
  valor: number;
  pix1: number;
  pix2: number;
  /** bonificação limitada ao teto de R$ 1,16 por kWh */
  teto: boolean;
  grupo_a: boolean;
  /** por que não entra na conta (valores zerados); null = entra */
  motivo_fora: string | null;
  /** 'YYYY-MM-10' do mês seguinte ao aceite */
  pix1_data_prevista: string;
}

const consumoDe = (v: number | string | null | undefined): number =>
  v == null || v === '' ? Number.NaN : Number(v);

/**
 * Bonificação de cada venda da regra nova, com a faixa da turma (parceiro dono
 * + mês do aceite). Vendas da regra antiga (aceitas antes de 26/10/2026) não
 * entram no resultado.
 */
export function calcularBonificacaoDasVendas(vendas: VendaBonificacao[]): Map<number, BonificacaoDaVenda> {
  const turmas = new Map<string, VendaBonificacao[]>();
  for (const v of vendas) {
    if (!v.dono_id || !ehRegraNova(v.decisao_final_at)) continue;
    const mes = mesAceite(v.decisao_final_at)!;
    const chave = `${v.dono_id}|${mes}`;
    const turma = turmas.get(chave) || [];
    turma.push(v);
    turmas.set(chave, turma);
  }

  const out = new Map<number, BonificacaoDaVenda>();
  for (const turma of turmas.values()) {
    // Ordem do aceite: o CPF/CNPJ repetido sai da segunda venda, não da primeira.
    turma.sort((a, b) =>
      (new Date(a.decisao_final_at).getTime() - new Date(b.decisao_final_at).getTime()) ||
      (a.negociacao_id - b.negociacao_id));

    const avaliadas = turma.map(v => {
      const grupoA = (v.ucs || []).some(u => isGrupoTarifarioA(u.grupo_tarifario));
      const desconto = descontoDaVenda(v.ucs);
      const motivoPrevio = grupoA ? MOTIVO_GRUPO_A : 'motivo' in desconto ? desconto.motivo : null;
      return { v, grupoA, d: 'd' in desconto ? desconto.d : null, motivoPrevio };
    });

    const naConta = avaliadas.filter(a => !a.motivoPrevio);
    const r = calcularBonificacao(naConta.map(a => ({ c: consumoDe(a.v.consumo_medio_kwh), d: a.d as number, doc: a.v.cpf_cnpj })));
    const linhaDe = new Map(naConta.map((a, i) => [a.v.negociacao_id, r.linhas[i]]));

    for (const a of avaliadas) {
      const mes = mesAceite(a.v.decisao_final_at)!;
      const l = linhaDe.get(a.v.negociacao_id);
      const entra = !!l && l.ok && !!r.fx;
      const consumo = consumoDe(a.v.consumo_medio_kwh);
      out.set(a.v.negociacao_id, {
        negociacao_id: a.v.negociacao_id,
        dono_id: a.v.dono_id,
        regra: REGRA_NOVA,
        mes_aceite: mes,
        consumo: Number.isFinite(consumo) ? consumo : 0,
        desconto_percentual: a.d,
        redutor: l && l.dOk ? redutor(l.d) : null,
        fatura_com_desconto: entra ? l!.F : 0,
        faixa_mes: r.fx ? `F${r.fx.i + 1}` : null,
        faixa_percentual: r.fx ? Math.round(r.fx.pct * 100) : 0,
        kwh_mes: r.V,
        valor: entra ? l!.B : 0,
        pix1: entra ? l!.E : 0,
        pix2: entra ? l!.S : 0,
        teto: entra ? l!.teto : false,
        grupo_a: a.grupoA,
        motivo_fora: a.motivoPrevio ?? (l && !l.ok ? motivoForaDaConta(l) : null),
        pix1_data_prevista: dataPrevistaPixI(mes),
      });
    }
  }
  return out;
}

/** Colunas de comissao_parceiros que a regra nova grava (sem nenhum status). */
export function colunasDaBonificacao(b: BonificacaoDaVenda) {
  return {
    regra: b.regra,
    mes_aceite: b.mes_aceite,
    consumo_medio_kwh: b.consumo,
    tarifa_energia_valor: REGRA_BONIFICACAO.tarifa,
    tarifa_energia_id: null,
    desconto_percentual: b.desconto_percentual,
    redutor: b.redutor,
    fatura_com_desconto: b.fatura_com_desconto,
    faixa_mes: b.faixa_mes,
    faixa_comissao_percentual: b.faixa_percentual,
    kwh_mes: b.kwh_mes,
    valor_base_comissao: b.valor,
    parcela_1_valor: b.pix1,
    parcela_1_data_prevista: b.pix1_data_prevista,
    parcela_2_valor: b.pix2,
    is_grupo_a: b.grupo_a,
    motivo_fora: b.motivo_fora,
  };
}

// ---------- o que gravar ----------

/** Colunas de status: a sincronização da Folha nunca reescreve numa linha que já existe. */
const COLUNAS_STATUS = new Set(['parcela_1_status', 'parcela_2_status', 'grupo_a_status']);

export interface LinhaGravada {
  id: number;
  parcela_1_status?: string | null;
  parcela_2_status?: string | null;
  [coluna: string]: unknown;
}

export type PlanoGravacao =
  | { acao: 'inserir'; dados: Record<string, unknown> }
  | { acao: 'atualizar'; id: number; dados: Record<string, unknown> }
  | { acao: 'manter'; motivo: 'pago' | 'igual' };

const vazio = (x: unknown) => x == null || x === '';

const iguais = (a: unknown, b: unknown): boolean => {
  if (vazio(a) || vazio(b)) return vazio(a) && vazio(b);
  if (typeof a === 'number' || typeof b === 'number') {
    const x = Number(a);
    const y = Number(b);
    return Number.isFinite(x) && Number.isFinite(y) && Math.abs(x - y) < 0.005;
  }
  return String(a) === String(b);
};

/**
 * Decide o que fazer com uma linha de comissao_parceiros:
 *   - não existe → insere valores + status iniciais;
 *   - alguma parcela já paga → não mexe em nada (o valor pago é o valor);
 *   - senão → atualiza só os valores que mudaram (a faixa do mês muda quando
 *     entra mais consumidor), nunca os status.
 */
export function planejarGravacao(
  existente: LinhaGravada | null | undefined,
  valores: Record<string, unknown>,
  statusIniciais: Record<string, unknown>,
): PlanoGravacao {
  const soValores = Object.fromEntries(Object.entries(valores).filter(([k]) => !COLUNAS_STATUS.has(k)));
  if (!existente) return { acao: 'inserir', dados: { ...soValores, ...statusIniciais } };
  if (existente.parcela_1_status === 'pago' || existente.parcela_2_status === 'pago') return { acao: 'manter', motivo: 'pago' };
  const mudou = Object.fromEntries(Object.entries(soValores).filter(([k, v]) => v !== undefined && !iguais(existente[k], v)));
  if (Object.keys(mudou).length === 0) return { acao: 'manter', motivo: 'igual' };
  return { acao: 'atualizar', id: existente.id, dados: mudou };
}

// ---------- Pix II: 1ª fatura paga ----------

export interface UcComNegociacao {
  negociacao_id: number;
  numero_uc?: string | null;
  outro_numero_uc?: string | null;
}

export interface FaturaParaPixII {
  uc_codigo_norm?: string | null;
  uc_codigo?: string | null;
  status_boleto_vigor?: string | null;
  status_fatura?: string | null;
  data_pgto_boleto?: string | null;
}

/** Fatura paga = boleto Pago/Quitado com data de pagamento, e não excluída. */
export function faturaPaga(f: FaturaParaPixII): boolean {
  return (f.status_boleto_vigor === 'Pago' || f.status_boleto_vigor === 'Quitado')
    && !!f.data_pgto_boleto
    && f.status_fatura !== 'Excluído';
}

/** Data do 1º pagamento de fatura de cada negociação (casando pelo número da UC normalizado). */
export function primeiroPagamentoPorNegociacao(ucs: UcComNegociacao[], faturas: FaturaParaPixII[]): Map<number, string> {
  const negPorUc = new Map<string, number>();
  for (const u of ucs) {
    for (const k of [normalizeUcCodigo(u.numero_uc), normalizeUcCodigo(u.outro_numero_uc)]) {
      if (k) negPorUc.set(k, u.negociacao_id);
    }
  }
  const out = new Map<number, string>();
  for (const f of faturas) {
    if (!faturaPaga(f)) continue;
    const neg = negPorUc.get(f.uc_codigo_norm || normalizeUcCodigo(f.uc_codigo));
    if (neg == null) continue;
    const atual = out.get(neg);
    if (!atual || new Date(f.data_pgto_boleto!).getTime() < new Date(atual).getTime()) out.set(neg, f.data_pgto_boleto!);
  }
  return out;
}

export interface LinhaAguardandoFatura {
  id: number;
  negociacao_id: number;
  regra?: string | null;
  parcela_2_status?: string | null;
  parcela_2_valor?: number | string | null;
}

/**
 * Linhas da regra nova cujo Pix II pode ser liberado: parcela 2 ainda esperando
 * a 1ª fatura, com valor, e negociação que já tem fatura paga. Vencimento:
 * dia 10 do mês seguinte ao pagamento.
 */
export function liberacoesPixII(
  linhas: LinhaAguardandoFatura[],
  primeiroPagamento: Map<number, string>,
): Array<{ id: number; parcela_2_status: 'pendente'; parcela_2_data_prevista: string }> {
  const out: Array<{ id: number; parcela_2_status: 'pendente'; parcela_2_data_prevista: string }> = [];
  for (const l of linhas) {
    if (!l.regra || l.parcela_2_status !== 'aguardando_primeira_fatura') continue;
    if (!(Number(l.parcela_2_valor) > 0)) continue;
    const pagamento = primeiroPagamento.get(l.negociacao_id);
    const data = pagamento ? dataPrevistaPixII(pagamento) : null;
    if (data) out.push({ id: l.id, parcela_2_status: 'pendente', parcela_2_data_prevista: data });
  }
  return out;
}

// ---------- texto ----------

/** "F3 · 45% · 6.752 kWh no mês" */
export function rotuloFaixaMes(faixa: string | null | undefined, pct: number | null | undefined, kwh: number | null | undefined): string {
  if (!faixa) return 'sem faixa no mês';
  return `${faixa} · ${Number(pct) || 0}% · ${Math.round(Number(kwh) || 0).toLocaleString('pt-BR')} kWh no mês`;
}
